import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  fetchUserOrganisatie,
  fetchUserOrganisatieUsers,
  updateUserOrganisatie,
  deleteUserOrganisatie,
  addLocationToOrganisatie,
  removeLocationFromOrganisatie,
  createOrganisatieUser,
  impersonateUser,
} from "./Reducers/actions";
import { startImpersonation } from "../../../Auth/impersonation";

/**
 * Genereer een sterk wachtwoord dat voldoet aan de signup-eisen
 * (min. 8 tekens, minstens één kleine letter, hoofdletter en cijfer).
 */
const generatePassword = () => {
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const digits = "23456789";
  const all = lower + upper + digits;
  const pick = (set) => set[Math.floor(Math.random() * set.length)];
  let pw = pick(lower) + pick(upper) + pick(digits);
  for (let i = 0; i < 7; i++) pw += pick(all);
  return pw;
};

/**
 * Inline formulier om direct een locatiebeheerder voor deze organisatie aan
 * te maken, zonder de omweg via Gebruikers > Toevoegen.
 */
const NewUserForm = ({ onCreate }) => {
  const empty = { voornaam: "", achternaam: "", email: "", telefoonnummer: "", password: "" };
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const set = (field, value) => setUser((prev) => ({ ...prev, [field]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setDone(null);
    if (!user.email || !user.voornaam || !user.achternaam || !user.password) {
      setError("Vul minimaal voornaam, achternaam, e-mail en wachtwoord in.");
      return;
    }
    setBusy(true);
    try {
      const telefoonnummer = (user.telefoonnummer || "").replace(/\s/g, "");
      const payload = { ...user };
      // Lege telefoon niet meesturen: de signup-validatie keurt een lege
      // string af (alleen undefined wordt overgeslagen).
      if (telefoonnummer) payload.telefoonnummer = telefoonnummer;
      else delete payload.telefoonnummer;
      await onCreate(payload);
      setDone(`Gebruiker ${user.email} aangemaakt. Er is een activatie-e-mail verstuurd.`);
      setUser(empty);
      setOpen(false);
    } catch (err) {
      setError(err.response?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="mt-3">
        {done && <div className="alert alert-success">{done}</div>}
        <button className="btn btn-primary" onClick={() => { setOpen(true); setDone(null); }}>
          + Nieuwe gebruiker aanmaken
        </button>
      </div>
    );
  }

  return (
    <form className="mt-3 border rounded p-3" onSubmit={submit}>
      <h6>Nieuwe locatiebeheerder voor deze organisatie</h6>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="row">
        <div className="col-md-6 form-group">
          <label>Voornaam *</label>
          <input className="form-control" value={user.voornaam} onChange={(e) => set("voornaam", e.target.value)} />
        </div>
        <div className="col-md-6 form-group">
          <label>Achternaam *</label>
          <input className="form-control" value={user.achternaam} onChange={(e) => set("achternaam", e.target.value)} />
        </div>
        <div className="col-md-6 form-group">
          <label>E-mail *</label>
          <input type="email" className="form-control" value={user.email} onChange={(e) => set("email", e.target.value)} />
        </div>
        <div className="col-md-6 form-group">
          <label>Telefoonnummer</label>
          <input className="form-control" value={user.telefoonnummer} onChange={(e) => set("telefoonnummer", e.target.value)} />
        </div>
        <div className="col-md-12 form-group">
          <label>Wachtwoord *</label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              value={user.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder="Min. 8 tekens, hoofd-/kleine letter en cijfer"
            />
            <button type="button" className="btn btn-outline-secondary" onClick={() => set("password", generatePassword())}>
              Genereer
            </button>
          </div>
        </div>
      </div>
      <button type="submit" className="btn btn-success" disabled={busy}>
        {busy ? "Bezig..." : "Aanmaken"}
      </button>
      <button type="button" className="btn btn-secondary ml-2" onClick={() => { setOpen(false); setError(null); }}>
        Annuleren
      </button>
    </form>
  );
};

/**
 * Locatie-zoeker: gebruikt /api/items/search/locations om locaties van het
 * eigen bedrijf op te zoeken zonder dat we de hele lijst hoeven te laden.
 */
const LocationPicker = ({ onPick, exclude = [] }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch("/api/items/search/locations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token"),
        },
        body: JSON.stringify({ query: q }),
      });
      const data = await res.json();
      const items = (data.results || []).filter(
        (r) => !exclude.includes(r._id)
      );
      setResults(items);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <form className="input-group" onSubmit={search}>
        <input
          type="text"
          className="form-control"
          placeholder="Zoek locatie op naam"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? "..." : "Zoek"}
        </button>
      </form>
      {results.length > 0 && (
        <ul className="list-group mt-2">
          {results.map((r) => (
            <li key={r._id} className="list-group-item d-flex justify-content-between align-items-center">
              <span>{r.what?.title?.value || r._id}</span>
              <button
                className="btn btn-sm btn-success"
                onClick={() => {
                  onPick(r._id);
                  setResults(results.filter((x) => x._id !== r._id));
                }}
              >
                Koppelen
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const UserOrganisatieEditPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const item = useSelector((s) => s.userorganisaties.item);
  const users = useSelector((s) => s.userorganisaties.users);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchUserOrganisatie(id));
    dispatch(fetchUserOrganisatieUsers(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (item && (!form || form._id !== item._id)) {
      setForm({
        _id: item._id,
        naam: item.naam || "",
        contact: item.contact || {},
        adres: item.adres || {},
        actief: !!item.actief,
      });
    }
  }, [item]); // eslint-disable-line

  if (!item || !form) {
    return <div className="p-3">Laden...</div>;
  }

  const setField = (path, value) => {
    const parts = path.split(".");
    setForm((prev) => {
      const next = { ...prev };
      if (parts.length === 1) next[parts[0]] = value;
      else next[parts[0]] = { ...next[parts[0]], [parts[1]]: value };
      return next;
    });
  };

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await dispatch(
        updateUserOrganisatie(id, {
          naam: form.naam,
          contact: form.contact,
          adres: form.adres,
          actief: form.actief,
        })
      );
    } catch (err) {
      setError(err.response?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const addLocation = async (locationId) => {
    setError(null);
    try {
      await dispatch(addLocationToOrganisatie(id, locationId));
    } catch (err) {
      setError(err.response?.error || err.message);
    }
  };

  const removeLocation = async (locationId) => {
    if (!window.confirm("Locatie loskoppelen van deze organisatie?")) return;
    setError(null);
    try {
      await dispatch(removeLocationFromOrganisatie(id, locationId));
    } catch (err) {
      setError(err.response?.error || err.message);
    }
  };

  const createUser = async (newUser) => {
    await dispatch(createOrganisatieUser(id, item.bedrijf, newUser));
    dispatch(fetchUserOrganisatieUsers(id));
  };

  const loginAs = async (u) => {
    if (!window.confirm(`Tijdelijk inloggen als ${u.voornaam} ${u.achternaam}? Je kunt daarna terug naar je eigen account.`)) return;
    setError(null);
    try {
      const data = await dispatch(impersonateUser(id, u._id));
      if (data?.token) {
        startImpersonation(data.token, `${u.voornaam} ${u.achternaam} — ${item.naam}`);
      }
    } catch (err) {
      setError(err.response?.error || err.message || "Inloggen als gebruiker mislukt");
    }
  };

  const remove = async () => {
    if (!window.confirm("Organisatie definitief verwijderen?")) return;
    try {
      await dispatch(deleteUserOrganisatie(id));
      navigate("/admin/userorganisaties");
    } catch (err) {
      if (err.status === 409 && window.confirm(`${err.response?.error}\n\nForceren (cascade)?`)) {
        await dispatch(deleteUserOrganisatie(id, true));
        navigate("/admin/userorganisaties");
      } else {
        setError(err.response?.error || err.message);
      }
    }
  };

  const linkedLocationIds = (item.locaties || []).map((l) =>
    typeof l === "string" ? l : l._id
  );

  return (
    <div>
      <div className="box box-default">
        <div className="box-header with-border d-flex justify-content-between">
          <h3 className="box-title">Organisatie bewerken</h3>
          <Link to="/admin/userorganisaties" className="btn btn-sm btn-secondary">
            ← Terug
          </Link>
        </div>
        <div className="box-body">
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="form-group">
            <label>Naam *</label>
            <input
              type="text"
              className="form-control"
              value={form.naam}
              onChange={(e) => setField("naam", e.target.value)}
            />
          </div>

          <h5 className="mt-3">Contact</h5>
          <div className="row">
            <div className="col-md-6 form-group">
              <label>Email</label>
              <input
                type="email"
                className="form-control"
                value={form.contact.email || ""}
                onChange={(e) => setField("contact.email", e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>Telefoon</label>
              <input
                type="text"
                className="form-control"
                value={form.contact.telefoon || ""}
                onChange={(e) => setField("contact.telefoon", e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>KvK</label>
              <input
                type="text"
                className="form-control"
                value={form.contact.kvk || ""}
                onChange={(e) => setField("contact.kvk", e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>Website</label>
              <input
                type="text"
                className="form-control"
                value={form.contact.website || ""}
                onChange={(e) => setField("contact.website", e.target.value)}
              />
            </div>
          </div>

          <h5 className="mt-3">Adres</h5>
          <div className="row">
            <div className="col-md-6 form-group">
              <label>Straat</label>
              <input
                type="text"
                className="form-control"
                value={form.adres.straat || ""}
                onChange={(e) => setField("adres.straat", e.target.value)}
              />
            </div>
            <div className="col-md-3 form-group">
              <label>Postcode</label>
              <input
                type="text"
                className="form-control"
                value={form.adres.postcode || ""}
                onChange={(e) => setField("adres.postcode", e.target.value)}
              />
            </div>
            <div className="col-md-3 form-group">
              <label>Woonplaats</label>
              <input
                type="text"
                className="form-control"
                value={form.adres.woonplaats || ""}
                onChange={(e) => setField("adres.woonplaats", e.target.value)}
              />
            </div>
          </div>

          <div className="form-check mt-3">
            <input
              id="actief"
              type="checkbox"
              className="form-check-input"
              checked={form.actief}
              onChange={(e) => setField("actief", e.target.checked)}
            />
            <label htmlFor="actief" className="form-check-label">
              Actief
            </label>
          </div>

          <div className="mt-3">
            <button onClick={save} className="btn btn-success" disabled={saving}>
              {saving ? "Bezig..." : "Opslaan"}
            </button>
            <button onClick={remove} className="btn btn-danger ml-2 float-right">
              Verwijderen
            </button>
          </div>
        </div>
      </div>

      <div className="box box-default mt-3">
        <div className="box-header with-border">
          <h3 className="box-title">Locaties ({(item.locaties || []).length})</h3>
        </div>
        <div className="box-body">
          {(item.locaties || []).length === 0 ? (
            <p className="text-muted">Nog geen locaties gekoppeld.</p>
          ) : (
            <ul className="list-group mb-3">
              {item.locaties.map((loc) => (
                <li key={loc._id || loc} className="list-group-item d-flex justify-content-between align-items-center">
                  <span>{loc.what?.title?.value || loc._id || loc}</span>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeLocation(loc._id || loc)}
                  >
                    Loskoppelen
                  </button>
                </li>
              ))}
            </ul>
          )}
          <h6>Locatie toevoegen</h6>
          <LocationPicker onPick={addLocation} exclude={linkedLocationIds} />
        </div>
      </div>

      <div className="box box-default mt-3">
        <div className="box-header with-border">
          <h3 className="box-title">Gekoppelde gebruikers ({users.length})</h3>
        </div>
        <div className="box-body">
          {users.length === 0 ? (
            <p className="text-muted">Nog geen gebruikers gekoppeld.</p>
          ) : (
            <ul className="list-group">
              {users.map((u) => (
                <li key={u._id} className="list-group-item d-flex justify-content-between align-items-center">
                  <span>
                    <strong>{u.voornaam} {u.achternaam}</strong>{" "}
                    <span className="text-muted">{u.email}</span>
                    {u.blocked && <span className="badge badge-warning ml-2">geblokkeerd</span>}
                  </span>
                  <span className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
                    {u.role === "locatiebeheerder" && !u.blocked && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => loginAs(u)}
                        title="Tijdelijk inloggen als deze gebruiker om te zien wat men ziet"
                      >
                        <i className="fa fa-user-secret" /> Inloggen als
                      </button>
                    )}
                    <Link to={`/admin/gebruikers/${u._id}`} className="btn btn-sm btn-outline-primary">
                      Bewerken
                    </Link>
                  </span>
                </li>
              ))}
            </ul>
          )}

          <NewUserForm onCreate={createUser} />
        </div>
      </div>
    </div>
  );
};

export default UserOrganisatieEditPage;
